<script lang="ts">
  /**
   * Per-attachment viewer + management UI for the MemoryDetail panel.
   *
   * Renders a list of attachments grouped by kind. Inline viewers for
   * image / video / audio / pdf; download links for everything else.
   * 3D models (glb/usdz/obj) get a "open in external viewer" link since
   * embedding model-viewer would add ~500KB to the bundle for a feature
   * that's used rarely. v1 can add it as a lazy-loaded chunk if needed.
   *
   * Add-attachment: a single URI input with auto-detected kind. The user
   * fills the URI, optionally tweaks kind/label, hits ADD. Idempotent on
   * the server, so re-adding the same URI is a no-op.
   *
   * Remove: per-row ✕ button (only visible when writeAvailable). DELETE
   * goes through addAttachment / removeAttachment in api.svelte.ts which
   * re-fetches the memory after each write to keep state consistent.
   */

  import { onMount } from 'svelte'
  import {
    addAttachment, removeAttachment,
    inferAttachmentKind, basenameFromUri,
    WritesDisabledError,
    type Attachment, type AttachmentKind, type MemoryDetail,
  } from './api.svelte'

  /** model-viewer is ~500KB. Lazy-load it on demand the first time a 3D
   * attachment renders, so memories without 3D content don't pay the
   * bundle cost. The web component registers itself globally on import. */
  let modelViewerReady = $state(false)
  let modelViewerLoading = false
  async function ensureModelViewer(): Promise<void> {
    if (modelViewerReady || modelViewerLoading) return
    modelViewerLoading = true
    try {
      await import('@google/model-viewer')
      modelViewerReady = true
    } catch (err) {
      console.warn('[mu-app] model-viewer load failed:', (err as Error).message)
    } finally {
      modelViewerLoading = false
    }
  }

  type Props = {
    detail: MemoryDetail
    writeAvailable: boolean
    onUpdate: (next: MemoryDetail) => void
  }
  let { detail, writeAvailable, onUpdate }: Props = $props()

  let addOpen = $state(false)
  let newUri = $state('')
  let newKind = $state<AttachmentKind>('link')
  let newLabel = $state('')
  let working = $state(false)
  let error = $state<string | null>(null)
  let removingIdx = $state<number | null>(null)

  const attachments = $derived<Attachment[]>(detail.attachments ?? [])
  /* Trigger model-viewer load whenever a 3D attachment is in the list. */
  $effect(() => {
    if (attachments.some((a) => a.kind === 'model3d')) {
      void ensureModelViewer()
    }
  })

  function startAdd(): void {
    addOpen = true
    newUri = ''
    newLabel = ''
    newKind = 'link'
    error = null
  }
  function cancelAdd(): void {
    addOpen = false
    newUri = ''
    newLabel = ''
    error = null
  }

  function onUriInput(): void {
    /* Auto-detect kind from extension as the user types — but don't
     * overwrite if they've manually picked a kind already. */
    if (newUri.trim().length > 0) {
      newKind = inferAttachmentKind(newUri.trim())
    }
  }

  async function submitAdd(): Promise<void> {
    if (working) return
    const uri = newUri.trim()
    if (!uri) { error = 'URI is required'; return }
    working = true
    error = null
    try {
      const updated = await addAttachment(detail.id, {
        uri,
        kind: newKind,
        label: newLabel.trim() || undefined,
      })
      onUpdate(updated)
      cancelAdd()
    } catch (err) {
      error = err instanceof WritesDisabledError
        ? 'Writes disabled on API'
        : (err as Error).message
    } finally {
      working = false
    }
  }

  async function remove(idx: number): Promise<void> {
    if (removingIdx !== null) return
    removingIdx = idx
    try {
      const updated = await removeAttachment(detail.id, idx)
      onUpdate(updated)
    } catch (err) {
      error = err instanceof WritesDisabledError
        ? 'Writes disabled on API'
        : (err as Error).message
    } finally {
      removingIdx = null
    }
  }

  function onKey(e: KeyboardEvent): void {
    if (!addOpen) return
    if (e.key === 'Escape') { e.preventDefault(); cancelAdd() }
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault(); void submitAdd()
    }
  }

  function fmtBytes(n: number | undefined): string {
    if (n == null) return ''
    if (n < 1024) return `${n} B`
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
    if (n < 1024 * 1024 * 1024) return `${(n / 1048576).toFixed(1)} MB`
    return `${(n / 1073741824).toFixed(2)} GB`
  }
</script>

<svelte:window onkeydown={onKey} />

<div class="md-section md-attachments">
  <header class="md-section-head">
    <span class="md-label">attachments</span>
    <span class="md-section-meta">
      {attachments.length}
      {#if attachments.length > 0}
        · click viewers · ✕ to remove
      {/if}
    </span>
    {#if writeAvailable && !addOpen}
      <button class="att-add-btn" type="button" onclick={startAdd} title="Add a URI reference">+ ADD</button>
    {/if}
  </header>

  {#if addOpen}
    <div class="att-add">
      <label class="att-field">
        <span class="att-k">uri</span>
        <input
          class="att-input"
          type="url"
          placeholder="https://… · file:// · /content/foo.pdf"
          bind:value={newUri}
          oninput={onUriInput}
          autocomplete="off"
          spellcheck="false"
        />
      </label>
      <div class="att-row">
        <label class="att-field">
          <span class="att-k">kind</span>
          <select class="att-input" bind:value={newKind}>
            <option value="image">image</option>
            <option value="video">video</option>
            <option value="audio">audio</option>
            <option value="pdf">pdf</option>
            <option value="model3d">3d model</option>
            <option value="document">document</option>
            <option value="archive">archive</option>
            <option value="data">data</option>
            <option value="link">link</option>
          </select>
        </label>
        <label class="att-field att-field-flex">
          <span class="att-k">label</span>
          <input class="att-input" type="text" placeholder="optional" bind:value={newLabel} maxlength="200" />
        </label>
      </div>
      {#if error}<div class="att-error">{error}</div>{/if}
      <div class="att-actions">
        <span class="att-shortcut">⌘↵ to add · ⎋ to cancel</span>
        <button class="att-btn" type="button" onclick={cancelAdd}>CANCEL</button>
        <button class="att-btn att-btn-primary" type="button" disabled={working} onclick={() => void submitAdd()}>
          {working ? 'ADDING…' : 'ADD'}
        </button>
      </div>
    </div>
  {/if}

  {#if attachments.length === 0 && !addOpen}
    <div class="att-empty">— no attachments —</div>
  {/if}

  <div class="att-list">
    {#each attachments as a, idx (a.uri)}
      <article class="att-item" data-kind={a.kind}>
        <header class="att-item-head">
          <span class="att-kind-pill" data-kind={a.kind}>{a.kind}</span>
          <span class="att-label">{a.label ?? basenameFromUri(a.uri)}</span>
          {#if a.size != null}
            <span class="att-meta">{fmtBytes(a.size)}</span>
          {/if}
          <a class="att-uri" href={a.uri} target="_blank" rel="noreferrer noopener" title={a.uri}>↗</a>
          {#if writeAvailable}
            <button
              class="att-remove"
              type="button"
              disabled={removingIdx !== null}
              onclick={() => void remove(idx)}
              title="Remove this attachment"
            >{removingIdx === idx ? '◌' : '✕'}</button>
          {/if}
        </header>

        <!-- Per-kind viewer -->
        {#if a.kind === 'image'}
          <a class="att-viewer-img-link" href={a.uri} target="_blank" rel="noreferrer noopener">
            <img class="att-viewer-img" src={a.uri} alt={a.label ?? ''} loading="lazy" />
          </a>
        {:else if a.kind === 'video'}
          <video class="att-viewer-video" controls preload="metadata" src={a.uri}>
            video element not supported · <a href={a.uri}>{a.uri}</a>
          </video>
        {:else if a.kind === 'audio'}
          <audio class="att-viewer-audio" controls preload="metadata" src={a.uri}>
            audio element not supported · <a href={a.uri}>{a.uri}</a>
          </audio>
        {:else if a.kind === 'pdf'}
          <iframe
            class="att-viewer-pdf"
            src={a.uri}
            title={a.label ?? 'PDF preview'}
            referrerpolicy="no-referrer"
          ></iframe>
        {:else if a.kind === 'model3d'}
          {#if modelViewerReady}
            <!-- model-viewer is a custom element registered by @google/model-viewer.
                 Svelte renders unknown tags fine; the element handles its own GL. -->
            {@html `<model-viewer
              src="${a.uri.replace(/"/g, '&quot;')}"
              alt="${(a.label ?? '3D model').replace(/"/g, '&quot;')}"
              camera-controls
              auto-rotate
              auto-rotate-delay="2000"
              shadow-intensity="0.8"
              exposure="0.9"
              style="width: 100%; height: 320px; background: #050505; --poster-color: transparent;"
              loading="lazy"
            ></model-viewer>`}
          {:else}
            <div class="att-viewer-3d-loading">
              <span class="att-viewer-3d-glyph">◇</span>
              <span>loading 3D viewer…</span>
              <a class="att-viewer-3d-link" href={a.uri} target="_blank" rel="noreferrer noopener">↗ OPEN</a>
            </div>
          {/if}
        {:else}
          <div class="att-viewer-link">
            <a href={a.uri} target="_blank" rel="noreferrer noopener">{a.uri}</a>
          </div>
        {/if}
      </article>
    {/each}
  </div>
</div>

<style>
  .md-attachments {
    margin-top: var(--s-3);
  }
  .att-add-btn {
    background: transparent;
    border: 1px solid var(--accent);
    color: var(--accent);
    font: inherit;
    font-size: 9px;
    letter-spacing: 0.1em;
    padding: 2px 8px;
    cursor: pointer;
    margin-left: var(--s-2);
    transition: background var(--t-fast), color var(--t-fast);
  }
  .att-add-btn:hover { background: var(--accent); color: var(--surface); }

  /* ── Add form ─────────────────────────────────────────────────── */
  .att-add {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: var(--s-2);
    border: 1px dashed var(--accent-soft);
    background: var(--surface-container-low);
    margin-bottom: var(--s-2);
  }
  .att-row { display: flex; gap: 6px; }
  .att-field { display: flex; flex-direction: column; gap: 2px; }
  .att-field-flex { flex: 1; }
  .att-k {
    font-size: 9px;
    color: var(--on-surface-faint);
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }
  .att-input {
    font: inherit;
    font-size: 11px;
    color: var(--on-surface);
    background: var(--surface-container);
    border: 1px solid var(--outline-variant);
    padding: 4px 6px;
    outline: none;
  }
  .att-input:focus { border-color: var(--accent); }
  .att-actions {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-top: 2px;
  }
  .att-shortcut {
    flex: 1;
    color: var(--on-surface-faint);
    font-size: 9px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }
  .att-btn {
    font: inherit;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    padding: 3px 10px;
    background: transparent;
    border: 1px solid var(--outline-variant);
    color: var(--on-surface-variant);
    cursor: pointer;
    font-size: 10px;
  }
  .att-btn:hover { color: var(--accent); border-color: var(--accent); }
  .att-btn-primary { color: var(--accent); border-color: var(--accent); }
  .att-btn-primary:hover { background: var(--accent-faint); color: var(--accent-bright); }
  .att-btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .att-error {
    color: #ff6b6b;
    font-size: 10px;
    border: 1px dashed #ff6b6b;
    padding: 3px 6px;
  }

  /* ── List + per-item ─────────────────────────────────────────── */
  .att-list {
    display: flex;
    flex-direction: column;
    gap: var(--s-2);
  }
  .att-empty {
    padding: var(--s-3) 0;
    text-align: center;
    color: var(--on-surface-faint);
    font-size: 10px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }
  .att-item {
    border: 1px solid var(--outline-variant);
    background: var(--surface-container-lowest);
  }
  .att-item-head {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 4px 6px;
    background: var(--surface-container-low);
    border-bottom: 1px solid var(--outline-variant);
    font-size: 10px;
  }
  .att-kind-pill {
    padding: 1px 5px;
    background: var(--surface-container);
    border: 1px solid var(--outline-variant);
    color: var(--on-surface-variant);
    font-size: 9px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    flex-shrink: 0;
  }
  .att-kind-pill[data-kind='image']    { color: #ffcc44; border-color: #b07000; }
  .att-kind-pill[data-kind='video']    { color: #ffb000; border-color: #b07000; }
  .att-kind-pill[data-kind='audio']    { color: #ffb000; border-color: #b07000; border-style: dashed; }
  .att-kind-pill[data-kind='pdf']      { color: #ff8b8b; border-color: #ff6b6b; }
  .att-kind-pill[data-kind='model3d']  { color: #88e0ff; border-color: #6FAED9; }
  .att-kind-pill[data-kind='document'] { color: #e8e8e8; border-color: #444; }
  .att-kind-pill[data-kind='archive']  { color: #888; border-color: #2A2A2A; }
  .att-kind-pill[data-kind='data']     { color: #88ff88; border-color: #4a8a4a; }
  .att-label {
    flex: 1;
    color: var(--on-surface);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .att-meta {
    color: var(--on-surface-faint);
    font-size: 9px;
  }
  .att-uri {
    color: var(--accent);
    text-decoration: none;
    font-size: 12px;
    line-height: 1;
    padding: 0 4px;
  }
  .att-uri:hover { color: var(--accent-bright); }
  .att-remove {
    background: transparent;
    border: 1px solid var(--outline-variant);
    color: var(--on-surface-faint);
    width: 18px; height: 18px;
    cursor: pointer;
    line-height: 1;
    font-size: 10px;
  }
  .att-remove:hover { color: #ff6b6b; border-color: #ff6b6b; }
  .att-remove:disabled { opacity: 0.5; cursor: not-allowed; }

  /* ── Per-kind viewers ───────────────────────────────────────── */
  .att-viewer-img-link { display: block; }
  .att-viewer-img {
    display: block;
    width: 100%;
    max-height: 320px;
    object-fit: contain;
    background: #000;
  }
  .att-viewer-video {
    display: block;
    width: 100%;
    max-height: 360px;
    background: #000;
  }
  .att-viewer-audio {
    display: block;
    width: 100%;
    padding: 6px;
  }
  .att-viewer-pdf {
    display: block;
    width: 100%;
    height: 480px;
    border: 0;
    background: #fff;
  }
  .att-viewer-3d-loading {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px;
    color: var(--on-surface-variant);
    font-size: 11px;
    background: var(--surface-container-lowest);
    letter-spacing: 0.06em;
    text-transform: uppercase;
    animation: att-pulse 1.4s ease-in-out infinite;
  }
  @keyframes att-pulse {
    0%, 100% { opacity: 0.55 }
    50%      { opacity: 1.0 }
  }
  .att-viewer-3d-glyph { color: #88e0ff; font-size: 16px; }
  .att-viewer-3d-text { flex: 1; }
  .att-viewer-3d-link {
    color: var(--accent);
    text-decoration: none;
    border: 1px solid var(--accent-soft);
    padding: 2px 8px;
    font-size: 10px;
    letter-spacing: 0.08em;
  }
  .att-viewer-3d-link:hover {
    background: var(--accent-faint);
    color: var(--accent-bright);
  }
  .att-viewer-link {
    padding: 8px;
    font-size: 10px;
  }
  .att-viewer-link a {
    color: var(--accent);
    text-decoration: none;
    word-break: break-all;
  }
  .att-viewer-link a:hover { color: var(--accent-bright); }
</style>
